import { FaPlus, FaTrash } from "react-icons/fa";
import { UnderlinedLinkButton } from "../../components/buttons";
import { ClientDropdown } from "../../components/dropdowns/client-dropdown";
import { StateDropdown } from "../../components/dropdowns/state-dropdown";
import { Column, Row } from "../../components/general";
import { HalfLabeledInput, LabeledInput } from "../../components/inputs";
import { BlackBody, RedH3 } from "../../components/text";
import colors from "../../constants/colors";
import { Case } from "../../models/case";

export const InfoTab = ({
  caseObject,
  setCaseObject,
  showRequiredErrors,
}: {
  caseObject: Case;
  setCaseObject: any;
  showRequiredErrors: boolean;
}) => {
  const transactionTypes: {
    value: Case["transactionType"];
    label: string;
    subLabel: string;
  }[] = [
    { value: "purchase", label: "Purchase", subLabel: "Buyer + Seller" },
    { value: "refinance", label: "Refinance", subLabel: "Borrower only" },
    { value: "cashSale", label: "Cash sale", subLabel: "No lender" },
    { value: "commercial", label: "Commercial", subLabel: "Entity buyer" },
  ];

  return (
    <div>
      <LabeledInput
        value={caseObject.caseNumber}
        setValue={(val: any) =>
          setCaseObject({ ...caseObject, caseNumber: val })
        }
        error={showRequiredErrors && !caseObject.caseNumber ? "Required." : ""}
      >
        File #
      </LabeledInput>

      <LabeledInput
        value={caseObject.address.line1}
        error={
          showRequiredErrors && !caseObject.address.line1 ? "Required." : ""
        }
        setValue={(val: any) =>
          setCaseObject({
            ...caseObject,
            address: { ...caseObject.address, line1: val },
          })
        }
      >
        Address line 1
      </LabeledInput>
      <LabeledInput
        value={caseObject.address.line2}
        setValue={(val: any) =>
          setCaseObject({
            ...caseObject,
            address: { ...caseObject.address, line2: val },
          })
        }
      >
        Address line 2 (optional)
      </LabeledInput>
      <LabeledInput
        value={caseObject.address.city}
        error={
          showRequiredErrors && !caseObject.address.city ? "Required." : ""
        }
        setValue={(val: any) =>
          setCaseObject({
            ...caseObject,
            address: { ...caseObject.address, city: val },
          })
        }
      >
        City
      </LabeledInput>

      <StateDropdown
        value={caseObject.address.state}
        error={
          showRequiredErrors && !caseObject.address.state ? "Required." : ""
        }
        setValue={(val: any) =>
          setCaseObject({
            ...caseObject,
            address: { ...caseObject.address, state: val },
          })
        }
      />

      <HalfLabeledInput
        value={caseObject.address.zip}
        setValue={(val: any) =>
          setCaseObject({
            ...caseObject,
            address: { ...caseObject.address, zip: val },
          })
        }
        error={
          showRequiredErrors
            ? !caseObject.address.zip
              ? "Required."
              : caseObject.address.zip.length != 5 ||
                isNaN(parseInt(caseObject.address.zip))
              ? "Please enter a valid zip code"
              : ""
            : ""
        }
      >
        Zip
      </HalfLabeledInput>

      <div className="p-field" style={{ marginBottom: 18, marginTop: 8 }}>
        <label className="p-label">Transaction type</label>
        <div className="p-radio-group">
          {transactionTypes.map((typeOption) => {
            const checked = caseObject.transactionType === typeOption.value;
            return (
              <button
                key={typeOption.value}
                type="button"
                className={`p-radio${checked ? " checked" : ""}`}
                onClick={() =>
                  setCaseObject({ ...caseObject, transactionType: typeOption.value })
                }
              >
                <span className="p-radio-dot" />
                <span className="p-radio-label">
                  {typeOption.label}
                  <span className="radio-meta">{typeOption.subLabel}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <LabeledInput
        value={caseObject.salePrice}
        setValue={(val: any) => setCaseObject({ ...caseObject, salePrice: val })}
      >
        Sale price
      </LabeledInput>
    </div>
  );
};

const ClientSelector = ({
  kind,
  client,
  setClient,
  title,
}: {
  kind:
    | "buyer"
    | "seller"
    | "agent"
    | "lender"
    | "titleAbstractor"
    | "surveyor";
  client: any;
  setClient: any;
  title: string;
}) => {
  return (
    <div>
      <RedH3 style={{ marginTop: 30, marginBottom: 12 }}>{title}</RedH3>
      <ClientDropdown
        kind={kind}
        value={client && client.id ? client.id : ""}
        setValue={(val: any) => {
          setClient(val);
        }}
        error={""}
      />

      {client && (client.additionalEmails || []).length > 0 && (
        <Column style={{ marginTop: 12 }}>
          <RedH3 style={{}}>Include emails:</RedH3>
          {(client.additionalEmails || []).map(
            (addl: { name: any; kind: any }, index: any) => (
              <Row style={{ marginTop: 8, alignItems: "center" }}>
                <BlackBody>{addl.name}</BlackBody>
                <BlackBody
                  style={{ marginLeft: 5 }}
                >{`(${addl.kind})`}</BlackBody>
                <UnderlinedLinkButton
                  style={{ marginLeft: 12, marginBottom: 4 }}
                  onClick={() => {
                    let buyersAgent = client;
                    buyersAgent.additionalEmails =
                      buyersAgent.additionalEmails.filter(
                        // @ts-ignore
                        (a, i) => i != index
                      );

                    setClient(buyersAgent);
                  }}
                >
                  remove
                </UnderlinedLinkButton>
              </Row>
            )
          )}
        </Column>
      )}
    </div>
  );
};

export const BuyersTab = ({
  caseObject,
  setCaseObject,
  showRequiredErrors,
}: {
  caseObject: Case;
  setCaseObject: any;
  showRequiredErrors: boolean;
}) => {
  return (
    <div>
      <ClientSelector
        kind={"agent"}
        client={caseObject.buyersAgent}
        setClient={(agent: any) =>
          setCaseObject({ ...caseObject, buyersAgent: agent })
        }
        title={"Buyer's Agent"}
      />

      <ClientSelector
        kind={"lender"}
        client={caseObject.lender}
        setClient={(agent: any) =>
          setCaseObject({ ...caseObject, lender: agent })
        }
        title={"Lender"}
      />
      <RedH3 style={{ marginTop: 40 }}>Buyers</RedH3>

      {caseObject.buyers.map((assistant, index) => (
        <Column
          className="p-subtle-block"
          style={{
            marginTop: 12,
            width: "100%",
          }}
        >
          <Row style={{ alignItems: "center" }}>
            <HalfLabeledInput
              value={assistant.name}
              inputStyles={{ width: 380, marginRight: 30 }}
              setValue={(val: any) => {
                let buyers = caseObject.buyers;
                buyers[index].name = val;

                setCaseObject({
                  ...caseObject,
                  buyers: buyers,
                });
              }}
            >
              Name
            </HalfLabeledInput>
            <HalfLabeledInput
              value={assistant.email}
              inputStyles={{ width: 380, marginRight: 30 }}
              setValue={(val: any) => {
                let buyers = caseObject.buyers;
                buyers[index].email = val;

                setCaseObject({
                  ...caseObject,
                  buyers: buyers,
                });
              }}
            >
              Email
            </HalfLabeledInput>
            <UnderlinedLinkButton
              style={{ display: "flex", alignItems: "center", height: 42, marginBottom: 0 }}
              onClick={() => {
                setCaseObject({
                  ...caseObject,
                  buyers: caseObject.buyers.filter((a, i) => i != index),
                });
              }}
            >
              <FaTrash />
            </UnderlinedLinkButton>
          </Row>
        </Column>
      ))}

      <UnderlinedLinkButton
        onClick={() => {
          setCaseObject({
            ...caseObject,
            buyers: caseObject.buyers.concat([{ name: "", email: "" }]),
          });
        }}
      >
        <FaPlus />
      </UnderlinedLinkButton>
    </div>
  );
};

export const SellersTab = ({
  caseObject,
  setCaseObject,
  showRequiredErrors,
}: {
  caseObject: Case;
  setCaseObject: any;
  showRequiredErrors: boolean;
}) => {
  return (
    <div>
      <ClientSelector
        kind={"agent"}
        client={caseObject.sellersAgent}
        setClient={(agent: any) =>
          setCaseObject({ ...caseObject, sellersAgent: agent })
        }
        title={"Seller's Agent"}
      />

      <RedH3 style={{ marginTop: 40 }}>Sellers</RedH3>

      {caseObject.sellers.map((assistant, index) => (
        <Column
          className="p-subtle-block"
          style={{
            marginTop: 12,
            width: "100%",
          }}
        >
          <Row style={{ alignItems: "center" }}>
            <HalfLabeledInput
              value={assistant.name}
              inputStyles={{ width: 380, marginRight: 30 }}
              setValue={(val: any) => {
                let sellers = caseObject.sellers;
                sellers[index].name = val;

                setCaseObject({
                  ...caseObject,
                  sellers: sellers,
                });
              }}
            >
              Name
            </HalfLabeledInput>
            <HalfLabeledInput
              value={assistant.email}
              inputStyles={{ width: 380, marginRight: 30 }}
              setValue={(val: any) => {
                let sellers = caseObject.sellers;
                sellers[index].email = val;

                setCaseObject({
                  ...caseObject,
                  sellers: sellers,
                });
              }}
            >
              Email
            </HalfLabeledInput>
            <UnderlinedLinkButton
              style={{ display: "flex", alignItems: "center", height: 42, marginBottom: 0 }}
              onClick={() => {
                setCaseObject({
                  ...caseObject,
                  sellers: caseObject.sellers.filter((a, i) => i != index),
                });
              }}
            >
              <FaTrash />
            </UnderlinedLinkButton>
          </Row>
        </Column>
      ))}

      <UnderlinedLinkButton
        onClick={() => {
          setCaseObject({
            ...caseObject,
            sellers: caseObject.sellers.concat([{ name: "", email: "" }]),
          });
        }}
      >
        <FaPlus />
      </UnderlinedLinkButton>
    </div>
  );
};

export const TemplatesTab = ({
  caseObject,
  setCaseObject,
  showRequiredErrors,
}: {
  caseObject: Case;
  setCaseObject: any;
  showRequiredErrors: boolean;
}) => {
  return (
    <div>
      <p>checkboxes.... select which emails to send and docs to upload</p>
    </div>
  );
};
